import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Chip,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import { CustomerSupport } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";
import { fDateTime } from "../../utils/formatTime";
import dayjs from "dayjs";
import { capitalizeFirstLetter } from "../../utils/helpers";
import { Loader } from "../../components/CustomLoader";

export default function ViewCustomerSupport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replying, setReplying] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [replyError, setReplyError] = useState("");

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await CustomerSupport.getById({ id });

      if (res.status === 200) {
        setData(res?.data?.data);

        const lastReply =
          res?.data?.data?.replyMessage

        setReplyMessage(lastReply || "");
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim()) {
      setReplyError("Reply message is required.");
      return;
    }

    try {
      setReplying(true);
      setReplyError("");

      const res = await CustomerSupport.reply({
        id,
        replyMessage: replyMessage.trim(),
      });

      if (res.status === 200) {
        toast.success(res?.data?.message || "Reply sent successfully");
        fetchData();
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      setReplying(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Loader />
      </Box>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent>
          <Typography>No data found</Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/customer-support")}
            sx={{ mt: 2 }}
          >
            Back to List
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { name, email, subject, message, queryStatus, createdAt, adminReplies = [] } =
    data;

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Typography variant="h4">Customer Support Request</Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/customer-support")}
            >
              Back to List
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Customer Info */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Customer Information" />
        <CardContent>
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }} gap={2}>
            <Box>
              <Typography variant="body2">Name</Typography>
              <Typography fontWeight={500}>
                {capitalizeFirstLetter(name || "N/A")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2">Email</Typography>
              <Typography fontWeight={500}>{email || "N/A"}</Typography>
            </Box>

            <Box>
              <Typography variant="body2">Subject</Typography>
              <Typography fontWeight={500}>
                {capitalizeFirstLetter(subject || "N/A")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2">Query Status</Typography>
              <Chip
                label={capitalizeFirstLetter(queryStatus || "N/A")}
                color={
                  queryStatus === "resolved"
                    ? "success"
                    : queryStatus === "inProgress"
                    ? "info"
                    : "warning"
                }
                size="small"
              />
            </Box>

            <Box>
              <Typography variant="body2">Created At</Typography>
              <Typography fontWeight={500}>{fDateTime(createdAt)}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Conversation */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title="User Query" />
        <CardContent>
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            maxHeight="500px"
            overflow="auto"
            sx={{ pr: 1 }}
          >
            {/* Admin Replies */}
            {adminReplies
              .slice()
              .reverse()
              .map((reply, index) => (
                <Box key={index} display="flex" justifyContent="flex-end">
                  
                    <Typography>{reply.replyMessage}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {dayjs(reply.repliedAt).format("MMM D, YYYY h:mm A")}
                    </Typography>
                </Box>
              ))}

            {/* User Message — ONLY MESSAGE, NO NAME, NO ICON */}
            <Box display="flex" justifyContent="flex-start">
              <Box
                sx={{
                  maxWidth: "70%",
                  bgcolor: "grey.100",
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <Typography>{message}</Typography>

                <Typography variant="caption" color="text.secondary">
                  {dayjs(createdAt).format("MMM D, YYYY h:mm A")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Reply Box */}
      <Card>
        <CardHeader title="Send Reply" />
        <CardContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Type your reply here..."
              value={replyMessage}
              onChange={(e) => {
                setReplyMessage(e.target.value);
                setReplyError("");
              }}
              error={!!replyError}
              helperText={replyError}
            />

            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={replying ? <CircularProgress size={20} /> : <SendIcon />}
                onClick={handleReply}
                disabled={replying || !replyMessage.trim()}
                sx={{ minWidth: 120 }}
              >
                {replying ? "Sending..." : "Send Reply"}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
